import { tzOffset } from '@date-fns/tz';
import { isValid, parseISO } from 'date-fns';

import { ZonedDate } from './zonedDate';

/** `YYYY-MM-DD`, optionally with a `HH:mm[:ss[.SSS…]]` wall-clock time and no offset. */
const WALL_TIME =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d{1,3})\d*)?)?)?$/;

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/**
 * The instant a wall-clock time (given as its UTC-equivalent milliseconds) has in a zone.
 * A time a DST switch repeats is its first occurrence; a time it skips moves forward by
 * the gap (00:30 in a 00:00-01:00 gap is 01:30), as moment-timezone resolved both.
 */
function wallClockToInstant(wallMs: number, timeZone: string): number {
    const offsets = [
        tzOffset(timeZone, new Date(wallMs - DAY_MS)),
        tzOffset(timeZone, new Date(wallMs + DAY_MS)),
    ];
    const matching = offsets
        .map((offset) => wallMs - offset * MINUTE_MS)
        .filter(
            (instant) => wallMs - instant === tzOffset(timeZone, new Date(instant)) * MINUTE_MS
        );

    if (matching.length > 0) return Math.min(...matching);
    return wallMs - Math.min(...offsets) * MINUTE_MS;
}

/** The parts of a wall-clock string as UTC-equivalent milliseconds, or NaN when not a real time. */
function wallClockMs(match: RegExpExecArray): number {
    const [, year, month, day, hour, minute, second, fraction] = match;
    const parts = [
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour ?? 0),
        Number(minute ?? 0),
        Number(second ?? 0),
        Number((fraction ?? '0').padEnd(3, '0')),
    ] as const;
    const ms = Date.UTC(...parts);
    const check = new Date(ms);
    const rolledOver =
        check.getUTCFullYear() !== parts[0] ||
        check.getUTCMonth() !== parts[1] ||
        check.getUTCDate() !== parts[2] ||
        check.getUTCHours() !== parts[3] ||
        check.getUTCMinutes() !== parts[4] ||
        check.getUTCSeconds() !== parts[5];
    return rolledOver ? Number.NaN : ms;
}

/**
 * The value in `timeZone`. A string without an offset is a wall-clock time read in that
 * zone ("2026-06-10" is midnight in Oslo for `Europe/Oslo`); a string with an offset, a
 * Date and a number are instants, shown in the zone. The result is a `ZonedDate`, whose
 * getters and date-fns formatting read the zone's wall clock.
 *
 * The wall clock is converted with the zone's offsets alone, never through a Date in the
 * browser's zone, so a time that the browser's own DST switch skips still lands right.
 */
export function wallTimeInZone(value: Date | string | number, timeZone: string): ZonedDate {
    if (typeof value !== 'string') {
        return new ZonedDate(value instanceof Date ? value.getTime() : value, timeZone);
    }

    const match = WALL_TIME.exec(value);
    if (match) {
        const wallMs = wallClockMs(match);
        const instant = Number.isNaN(wallMs) ? Number.NaN : wallClockToInstant(wallMs, timeZone);
        return new ZonedDate(instant, timeZone);
    }

    const instant = parseISO(value);
    return new ZonedDate(isValid(instant) ? instant.getTime() : Number.NaN, timeZone);
}
